/**
 * Generador de PDF (vía window.print) para Recuadros R17–R21
 * Declaración de Renta AT 2026 - Régimen Pro PYME (14D3)
 */
import { formatCurrency } from '../../common/formatUtils';
import { r17 as r17Fn, r18 as r18Fn, r19 as r19Fn, r20 as r20Fn, r21 as r21Fn } from './formulas';

const fmt = (val) => {
  const result = formatCurrency(val);
  return result !== '' && result !== null && result !== undefined ? result : '0';
};

// -----------------------------------------------------------------------
// Helpers HTML
// -----------------------------------------------------------------------

const fieldRow = (sign, code, label, value, type = '') => {
  const cls = type ? ` row-${type}` : '';
  return `
    <div class="field-row${cls}">
      <span class="sign">${sign}</span>
      <span class="code">${code}</span>
      <span class="label">${label}</span>
      <span class="value">${value}</span>
    </div>`;
};

const subsectionHtml = (title, rows) => `
  <div class="subsection">
    <div class="subsection-title">${title}</div>
    ${rows.join('')}
  </div>`;

const sectionHtml = (title, subtitle, content) => `
  <div class="section">
    <div class="section-header">
      <span class="section-title">${title}</span>
      <span class="section-subtitle">${subtitle}</span>
    </div>
    <div class="section-body">${content}</div>
  </div>`;

// -----------------------------------------------------------------------
// R17 – Base Imponible
// -----------------------------------------------------------------------
const buildR17 = (data, computed) => {
  const ingresos = subsectionHtml('Ingresos', [
    fieldRow('(+)', '1400', 'Ingresos del giro percibidos',                           fmt(r17Fn.ingresosGiroPercibidos(data))),
    fieldRow('(+)', '1817', 'Ingresos devengados en ejercicios anteriores y percibidos', fmt(r17Fn.ingresosDevengadosPercibidos(data))),
    fieldRow('(+)', '1401', 'Rentas de fuente extranjera percibidas',                  fmt(r17Fn.rentasFuenteExtranjera(data))),
    fieldRow('(+)', '1587', 'Ingresos operaciones con empresas relacionadas (14A)',    fmt(r17Fn.ingresosRelacionados14A(data))),
    fieldRow('(+)', '1588', 'Otros ingresos percibidos o devengados',                  fmt(r17Fn.otrosIngresosPercibidos(data))),
    fieldRow('(=)', '1410', 'Total de ingresos anuales',                               fmt(computed.r17?.totalIngresos), 'subtotal'),
  ]);

  const egresos = subsectionHtml('Egresos', [
    fieldRow('(-)', '1409', 'Existencias, insumos y servicios pagados',                fmt(r17Fn.existenciasPagadas(data))),
    fieldRow('(-)', '1818', 'Gastos adeudados en ejercicios anteriores y pagados',     fmt(r17Fn.gastosAdeudadosPagados(data))),
    fieldRow('(-)', '1411', 'Remuneraciones pagadas',                                  fmt(r17Fn.remuneracionesPagadas(data))),
    fieldRow('(-)', '1412', 'Honorarios pagados',                                      fmt(r17Fn.honorariosPagados(data))),
    fieldRow('(-)', '1413', 'Adquisición bienes activo fijo, pagados',                 fmt(r17Fn.activoFijoPagado(data))),
    fieldRow('(-)', '1415', 'Arriendos pagados',                                       fmt(r17Fn.arriendosPagados(data))),
    fieldRow('(-)', '1419', 'Intereses y reajustes pagados',                           fmt(r17Fn.interesesPagados(data))),
    fieldRow('(-)', '1422', 'Partidas art. 21 inc. 1° y 2° LIR pagados',              fmt(r17Fn.partidasArt21(data))),
    fieldRow('(-)', '1424', 'Otros gastos deducibles',                                 fmt(r17Fn.otrosGastos(data))),
    fieldRow('(=)', '1430', 'Total de egresos anuales',                                fmt(computed.r17?.totalEgresos), 'subtotal'),
  ]);

  const resultado = subsectionHtml('Resultado', [
    fieldRow('(+)', '1431', 'Partidas inc. 1° art. 21 LIR (gastos rechazados)',        fmt(r17Fn.gastosRechazadosArt21(data))),
    fieldRow('(=)', '1729', 'BI antes de incentivo al ahorro',                         fmt(computed.r17?.biAntesIncentivo), 'highlight'),
    fieldRow('(-)', '1432', 'Incentivo al ahorro art. 14 letra E)',                    fmt(r17Fn.incentivoAhorro(data))),
    fieldRow('(=)', '1440', 'Base Imponible afecta a IDPC',                            fmt(computed.r17?.biAfectaIDPC), 'total'),
  ]);

  return sectionHtml(
    'Recuadro N° 17',
    'Base Imponible Régimen Pro PYME (Art. 14 letra D) N° 3 LIR)',
    ingresos + egresos + resultado
  );
};

// -----------------------------------------------------------------------
// R18 – Determinación del RAI
// -----------------------------------------------------------------------
const buildR18 = (data, computed) => {
  const rows = [
    fieldRow('(+)', '1703', 'CPTS positivo final (recuadro N° 19)',                   fmt(r18Fn.cptsPositivo(data))),
    fieldRow('(+)', '1704', 'Remesas, retiros o dividendos repartidos, históricos',    fmt(r18Fn.retirosHistoricos(data))),
    fieldRow('(=)', '1720', 'Subtotal',                                                fmt(r18Fn.subtotal(data)), 'subtotal'),
    fieldRow('(-)', '1493', 'Saldo positivo del registro REX antes de imputaciones',  fmt(r18Fn.saldoREXPositivo(data))),
    fieldRow('(-)', '1494', 'Capital aportado, histórico',                             fmt(r18Fn.capitalAportado(data))),
    fieldRow('(=)', '1500', 'Rentas afectas a IGC o IA (RAI) del ejercicio',           fmt(computed.r18?.rai), 'total'),
  ];
  const content = `<div class="subsection">${rows.join('')}</div>`;
  return sectionHtml(
    'Recuadro N° 18',
    'Determinación del RAI (Art. 14 letra D) N° 3 LIR)',
    content
  );
};

// -----------------------------------------------------------------------
// R19 – CPTS
// -----------------------------------------------------------------------
const buildR19 = (data, computed) => {
  const rows = [
    fieldRow('(+)', '1445', 'CPT o CPTS positivo inicial',                            fmt(r19Fn.cptInicial(data))),
    fieldRow('(+)', '1705', 'Base imponible afecta a IDPC del ejercicio',              fmt(r19Fn.biAfecta(data))),
    fieldRow('(+)', '1726', 'Retiros o dividendos percibidos en el ejercicio',         fmt(r19Fn.dividendosPercibidos(data))),
    fieldRow('(-)', '1479', 'Remesas, retiros o dividendos repartidos',                fmt(r19Fn.retiros(data))),
    fieldRow('(-)', '1708', 'Partidas inc. 1° art. 21 LIR',                           fmt(r19Fn.partidasArt21(data))),
    fieldRow('(-)', '1379', 'Crédito IPE contra IF del ejercicio',                    fmt(r19Fn.creditoIPE(data))),
    fieldRow('(+)', '1710', 'Incentivo al ahorro art. 14 letra E)',                   fmt(r19Fn.incentivoAhorro(data))),
    fieldRow('(=)', '1545', 'CPTS positivo final',                                    fmt(computed.r19?.cptsPositivoFinal), 'total'),
  ];
  const content = `<div class="subsection">${rows.join('')}</div>`;
  return sectionHtml(
    'Recuadro N° 19',
    'CPTS Régimen Pro PYME (Art. 14 letra D) N° 3 LIR)',
    content
  );
};

// -----------------------------------------------------------------------
// R20 – Registro RTRE (tabla RAI / INR / STUT)
// -----------------------------------------------------------------------
const buildR20 = (data) => {
  const rem    = r20Fn.remanente(data);
  const remSig = r20Fn.remanenteSiguiente(data);

  const tableRows = [
    { label: 'Remanente ejercicio anterior (saldo positivo)', code: '1451/1454/1382', values: rem },
    { label: 'Reversos y/o disminuciones (propios)',          code: '1459',           values: { RAI: r20Fn.reverso(data).RAI } },
    { label: 'Aumentos del ejercicio (propios)',              code: '1463',           values: { RAI: r20Fn.aumentoRAI(data).RAI } },
    { label: 'Otros aumentos del ejercicio',                  code: '1470/1387',      values: { REX_INR: r20Fn.aumentoINR(data).REX_INR } },
    { label: 'Retiros imputados a RTRE',                      code: '1475',           values: { RAI: r20Fn.retiros(data) } },
    { label: 'Remanente ejercicio siguiente',                 code: '1484/1487/1391', values: remSig, isTotal: true },
  ];

  const headerRow = `
    <tr>
      <th style="text-align:left">Detalle</th>
      <th>Código</th>
      <th>RAI</th>
      <th>INR</th>
      <th>STUT</th>
    </tr>`;

  const bodyRows = tableRows.map(r => {
    const cls = r.isTotal ? ' class="row-total"' : '';
    return `<tr${cls}>
      <td style="text-align:left">${r.label}</td>
      <td style="text-align:center;font-size:0.75em;color:#666">${r.code}</td>
      <td>${fmt(r.values.RAI)}</td>
      <td>${fmt(r.values.REX_INR)}</td>
      <td>${fmt(r.values.STUT)}</td>
    </tr>`;
  }).join('');

  const content = `
    <div class="table-wrapper">
      <table class="data-table">
        <thead>${headerRow}</thead>
        <tbody>${bodyRows}</tbody>
      </table>
    </div>`;

  return sectionHtml(
    'Recuadro N° 20',
    'Registro Tributario de Rentas Empresariales y Movimiento STUT',
    content
  );
};

// -----------------------------------------------------------------------
// R21 – Registro SAC
// -----------------------------------------------------------------------
const buildR21 = (data) => {
  const rem        = r21Fn.remanente(data);
  const idpcBI     = r21Fn.idpcBI(data);
  const idpcDiv    = r21Fn.idpcDividendo(data);
  const asigRet    = r21Fn.asignadoRetiros(data);
  const gastosR    = r21Fn.gastosRechazados(data);
  const remSig     = r21Fn.remanenteSiguiente(data);

  const sacRows = [
    { label: 'Remanente ejercicio anterior',      noSujetoSinDev: rem.SAC_noSujetoSinDev,    noSujetoConDev: rem.SAC_noSujetoConDev,    sujetoConDev: rem.SAC_sujetoConDev,    ipeSinDev: rem.SAC_IPE_sinDev,       sac2016: rem.SAC_2016_conDev },
    { label: 'IDPC e IPE base imponible',          noSujetoSinDev: idpcBI.SAC_IPE_sinDev,     noSujetoConDev: idpcBI.SAC_noSujetoConDev, sujetoConDev: 0,                       ipeSinDev: idpcBI.SAC_IPE_IF,        sac2016: 0 },
    { label: 'IDPC e IPE dividendos percibidos',  noSujetoSinDev: 0,                          noSujetoConDev: idpcDiv.SAC_noSujetoConDev, sujetoConDev: idpcDiv.SAC_sujetoConDev, ipeSinDev: 0,                       sac2016: idpcDiv.SAC_2016_conDev },
    { label: 'Asignado a retiros',                noSujetoSinDev: asigRet.SAC_noSujetoSinDev, noSujetoConDev: asigRet.SAC_noSujetoConDev, sujetoConDev: 0,                      ipeSinDev: asigRet.SAC_IPE,          sac2016: 0 },
    { label: 'Asignado a gastos rechazados',      noSujetoSinDev: 0,                          noSujetoConDev: gastosR.SAC_noSujetoConDev, sujetoConDev: 0,                       ipeSinDev: 0,                        sac2016: 0 },
    { label: 'Remanente ejercicio siguiente',     noSujetoSinDev: remSig.SAC_noSujetoSinDev,  noSujetoConDev: remSig.SAC_noSujetoConDev, sujetoConDev: remSig.SAC_sujetoConDev, ipeSinDev: remSig.SAC_IPE_sinDev,   sac2016: remSig.SAC_2016_conDev, isTotal: true },
  ];

  const headerRow = `
    <tr>
      <th style="text-align:left">Detalle</th>
      <th>No Suj. S/Dev</th>
      <th>No Suj. C/Dev</th>
      <th>Suj. C/Dev</th>
      <th>IPE</th>
      <th>Cred. 2016</th>
    </tr>`;

  const bodyRows = sacRows.map(r => {
    const cls = r.isTotal ? ' class="row-total"' : '';
    return `<tr${cls}>
      <td style="text-align:left">${r.label}</td>
      <td>${fmt(r.noSujetoSinDev)}</td>
      <td>${fmt(r.noSujetoConDev)}</td>
      <td>${fmt(r.sujetoConDev)}</td>
      <td>${fmt(r.ipeSinDev)}</td>
      <td>${fmt(r.sac2016)}</td>
    </tr>`;
  }).join('');

  const content = `
    <div class="table-wrapper">
      <table class="data-table">
        <thead>${headerRow}</thead>
        <tbody>${bodyRows}</tbody>
      </table>
    </div>`;

  return sectionHtml(
    'Recuadro N° 21',
    'Registro SAC (Art. 14 letra D) N° 3 LIR)',
    content
  );
};

// -----------------------------------------------------------------------
// CSS inline para el HTML imprimible
// -----------------------------------------------------------------------
const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11px;
    color: #1a1a1a;
    padding: 20px 30px;
  }
  .doc-header {
    border-bottom: 2px solid #2b6cb0;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }
  .doc-title {
    font-size: 15px;
    font-weight: 700;
    color: #1a202c;
  }
  .doc-subtitle {
    font-size: 11px;
    color: #4a5568;
    margin-top: 4px;
  }
  .section {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    margin-bottom: 14px;
    page-break-inside: avoid;
    overflow: hidden;
  }
  .section-header {
    background: #f7fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 8px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .section-title {
    font-size: 12px;
    font-weight: 700;
    color: #2d3748;
  }
  .section-subtitle {
    font-size: 10px;
    color: #718096;
  }
  .section-body {
    padding: 10px 12px;
  }
  .subsection {
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid #f0f0f0;
  }
  .subsection:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
  .subsection-title {
    font-size: 10px;
    font-weight: 700;
    color: #2d3748;
    padding-bottom: 4px;
    margin-bottom: 6px;
    border-bottom: 1px solid #e2e8f0;
  }
  .field-row {
    display: flex;
    align-items: center;
    padding: 3px 0;
    gap: 6px;
    min-height: 22px;
  }
  .sign {
    width: 26px;
    font-size: 10px;
    color: #718096;
    text-align: center;
    flex-shrink: 0;
  }
  .code {
    width: 42px;
    font-size: 9px;
    color: #a0aec0;
    text-align: center;
    font-family: monospace;
    flex-shrink: 0;
  }
  .label {
    flex: 1;
    font-size: 10px;
    color: #4a5568;
  }
  .value {
    width: 120px;
    text-align: right;
    font-family: monospace;
    font-size: 10px;
    font-weight: 500;
    flex-shrink: 0;
  }
  .row-subtotal {
    background: #f0f4ff;
    border-radius: 3px;
    padding: 4px 6px;
  }
  .row-subtotal .label,
  .row-subtotal .value { font-weight: 600; }
  .row-highlight {
    background: #fffbeb;
    border-radius: 3px;
    padding: 4px 6px;
  }
  .row-highlight .label { font-weight: 600; color: #92400e; }
  .row-total {
    background: #f0fff4;
    border: 1px solid #c6f6d5;
    border-radius: 3px;
    padding: 5px 6px;
  }
  .row-total .label,
  .row-total .value { font-weight: 700; font-size: 11px; color: #22543d; }
  /* Tables for R20/R21 */
  .table-wrapper { overflow-x: auto; }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
  }
  .data-table th {
    background: #f7fafc;
    border-bottom: 2px solid #2b6cb0;
    color: #2b6cb0;
    font-size: 9px;
    text-transform: uppercase;
    padding: 5px 6px;
    text-align: right;
    white-space: nowrap;
  }
  .data-table td {
    border-bottom: 1px solid #f0f0f0;
    padding: 4px 6px;
    text-align: right;
    font-family: monospace;
  }
  .data-table tr.row-total td {
    background: #f0f4ff;
    border-top: 2px solid #2b6cb0;
    font-weight: 700;
  }
  @media print {
    body { padding: 10px 15px; }
    .section { page-break-inside: avoid; }
  }
`;

// -----------------------------------------------------------------------
// Construye el HTML completo (reutilizable para PDF server-side)
// -----------------------------------------------------------------------
export const buildR17_R21_Html = (data, computed) => {
  const sectionsHtml = [
    buildR17(data, computed),
    buildR18(data, computed),
    buildR19(data, computed),
    buildR20(data),
    buildR21(data),
  ].join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>Declaración de Renta AT 2026 – Recuadros R17 a R21</title>
  <style>${CSS}</style>
</head>
<body>
  <div class="doc-header">
    <div class="doc-title">Declaración de Renta AT 2026 — Pro PYME (14D3)</div>
    <div class="doc-subtitle">Recuadros N° 17, 18, 19, 20 y 21 — Formulario 22</div>
  </div>
  ${sectionsHtml}
</body>
</html>`;
};

// -----------------------------------------------------------------------
// Función principal de exportación (browser print - mantener por compatibilidad)
// -----------------------------------------------------------------------
export const downloadR17_R21_Pdf = (data, computed) => {
  const html = buildR17_R21_Html(data, computed);

  const win = window.open('', '_blank');
  if (!win) {
    alert('No se pudo abrir la ventana de impresión. Verifique que el bloqueador de ventanas emergentes esté desactivado.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
};
